/**
 * Integration with Roster (https://getroster.com), the creator/affiliate attribution vendor.
 * index.html loads sa.js once OneTrust targeting consent (C0004) is granted, which tracks the
 * referral click on every page; this module reports the order that closes the loop.
 *
 * @see https://docs.getroster.com/generic-tracking-pixel-instructions
 */
import analytics from '.';
import AppPaths from '../../AppPaths';
import { getTenancy } from '../../hooks/useTenancy';
import { RosterOrder } from '../../types/Roster';
import store from '../reduxStore';
import { typedWindow } from '../Window';
import { ECOM_EVENT_ORDER_COMPLETED, SegmentPurchaseEventProperties } from './events';

/** Non-test production only, matching the other marketing pixels. */
function rosterEnabled(): boolean {
  return getTenancy() !== 'test' && process.env.REACT_APP_ENVIRONMENT === 'production';
}

/**
 * Roster pays creators commission on the whole order, so unlike the roas pixels this reports
 * every order rather than only ones holding a product from `isTrackedMarketingProduct`. That
 * allowlist exists for Meta catalog matching, so it drops microchips, upgrades, and any
 * accessory launched since it was last updated — all of which a creator still earns on.
 * Verizon checkout stays excluded, as it is for the other pixels: those orders come through
 * the carrier, never a creator link.
 */
function rosterTrackableOrder(): boolean {
  return rosterEnabled() && !window.location.pathname.includes(AppPaths.Verizon.Root);
}

/**
 * Builds the order payload. `subtotalPrice` is the field Roster computes commission from, and it
 * wants that figure post-discount and before tax and shipping.
 */
export function rosterOrderPayload(properties: SegmentPurchaseEventProperties): RosterOrder {
  // `total` is the roas figure — pre-discount and padded with free-trial subscription value — so
  // commissioning against it would pay creators on the discount their own code just produced.
  const subtotalPrice = properties.chargedSubtotal ?? properties.total;
  const traits = analytics.userTraits();
  const session = store.getState().session;
  const email = session?.email ?? traits.email;

  return {
    orderId: properties.orderId,
    orderDate: new Date().toISOString(),
    currency: properties.currency,
    subtotalPrice,
    totalPrice: properties.chargedTotal ?? subtotalPrice + (properties.shipping ?? 0) + (properties.tax ?? 0),
    ...(properties.code && { discountCodes: [properties.code] }),
    ...(email && { email }),
    ...(session?.userId && { customerId: session.userId }),
    ...(traits.firstName && { firstName: traits.firstName }),
    ...(traits.lastName && { lastName: traits.lastName }),
  };
}

function rosterOrderCompleted(_messageId: string, properties: SegmentPurchaseEventProperties) {
  if (!rosterTrackableOrder()) {
    return;
  }

  // fiRosterOrder queues until sa.js has loaded, so the confirmation page cannot lose this order
  // to the script still being in flight.
  typedWindow.fiRosterOrder?.(rosterOrderPayload(properties));
}

// Map Segment events to Roster handlers
type RosterEventHandler = (messageId: string, properties: any) => void;
const ROSTER_EVENT_MAP: Record<string, RosterEventHandler> = {
  [ECOM_EVENT_ORDER_COMPLETED]: rosterOrderCompleted,
};

function onTrack(event: string, properties: any, messageId: string) {
  const handler = ROSTER_EVENT_MAP[event];
  if (!handler) {
    return;
  }
  handler(messageId, properties);
}

/** Wire Order Completed to the Roster order pixel; safe to call pre-consent. */
export function rosterSetupCallbacks() {
  analytics.onTrack(onTrack);
}
